import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

export const HeroSectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

export const Title = styled.h1`
  color: #2B3857;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  text-shadow: 0 2px 4px rgba(43, 56, 87, 0.2);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const Subtitle = styled.p`
  color: #2B3857;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const Form = styled.form`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(43, 56, 87, 0.1);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const Label = styled.label`
  display: block;
  color: #2B3857;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(43, 56, 87, 0.2);
  border-radius: 8px;
  font-size: 1rem;
  color: #2B3857;
  background: rgba(248, 250, 252, 0.8);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2B3857;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 0 0 3px rgba(197, 51, 123, 0.1);
  }

  &::placeholder {
    color: rgba(43, 56, 87, 0.5);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(43, 56, 87, 0.2);
  border-radius: 8px;
  font-size: 1rem;
  color: #2B3857;
  background: rgba(248, 250, 252, 0.8);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #2B3857;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 0 0 3px rgba(197, 51, 123, 0.1);
  }

  &::placeholder {
    color: rgba(43, 56, 87, 0.5);
  }
`;

export const ServicesContainer = styled.div`
  margin: 2rem 0;
`;

export const ServiceCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(43, 56, 87, 0.15);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(197, 51, 123, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const ServiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const ServiceTitle = styled.h3`
  color: #2B3857;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
`;

export const ServiceActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.variant === 'danger' ? '#ef4444' : '#2B3857'};
  color: white;

  &:hover {
    background: ${props => props.variant === 'danger' ? '#dc2626' : '#1e293b'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ImageUpload = styled.div`
  position: relative;
  width: 100%;
`;

export const ImagePreview = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 200px;
  border: 2px dashed rgba(43, 56, 87, 0.3);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    border-color: #2B3857;
  }
`;

export const ImagePlaceholder = styled.div`
  width: 100%;
  max-width: 300px;
  height: 200px;
  border: 2px dashed rgba(43, 56, 87, 0.3);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(43, 56, 87, 0.6);
  transition: all 0.3s ease;

  &:hover {
    border-color: #2B3857;
    color: #2B3857;
  }

  span {
    margin-top: 0.5rem;
    font-size: 0.9rem;
  }
`;

export const ImageInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

export const UploadButton = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  background: rgba(43, 56, 87, 0.9);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #2B3857;
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #C5337B 0%, #E91E63 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(197, 51, 123, 0.4);
    
    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

export const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

export const PreviewSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(43, 56, 87, 0.15);
`;

export const PreviewTitle = styled.h2`
  color: #2B3857;
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: center;
`;

export const PreviewCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(43, 56, 87, 0.15);
  border-radius: 12px;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const PreviewImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

export const PreviewContent = styled.div`
  text-align: left;
`;

export const PreviewServiceTitle = styled.h3`
  color: #2B3857;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

export const PreviewServiceDescription = styled.p`
  color: #64748B;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: rgba(43, 56, 87, 0.6);
`;

export const EmptyStateIcon = styled.div`
  margin-bottom: 1rem;
`;

export const EmptyStateText = styled.p`
  font-size: 1rem;
  margin: 0;
`;
